import React from 'react';
import {useRef} from 'react';

import {motion} from 'framer-motion';
import {useInView} from 'framer-motion';
import {useIntl} from 'react-intl';

export const AdaptiveTraining: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const intl = useIntl();
  const ref = useRef(null);
  const isInView = useInView(ref, {once: true, amount: 0.3});

  const trainingFeatures = [
    {
      title: intl.formatMessage({id: 'home.adaptiveTraining.feature1Title'}),
      description: intl.formatMessage({
        id: 'home.adaptiveTraining.feature1Description',
      }),
      phase: 'Planning',
      image: '/images/adaptive-training/AdaptiveTraining4.png',
    },
    {
      title: intl.formatMessage({id: 'home.adaptiveTraining.feature2Title'}),
      description: intl.formatMessage({
        id: 'home.adaptiveTraining.feature2Description',
      }),
      phase: 'Social',
      image: '/images/adaptive-training/AdaptiveTraining2.png',
    },
    {
      title: intl.formatMessage({id: 'home.adaptiveTraining.feature3Title'}),
      description: intl.formatMessage({
        id: 'home.adaptiveTraining.feature3Description',
      }),
      phase: 'Periodization',
      image: '/images/adaptive-training/AdaptiveTraining3.png',
    },
    {
      title: intl.formatMessage({id: 'home.adaptiveTraining.feature4Title'}),
      description: intl.formatMessage({
        id: 'home.adaptiveTraining.feature4Description',
      }),
      phase: 'Flexibility',
      image: '/images/adaptive-training/AdaptiveTraining1.png',
    },
    {
      title: intl.formatMessage({id: 'home.adaptiveTraining.feature5Title'}),
      description: intl.formatMessage({
        id: 'home.adaptiveTraining.feature5Description',
      }),
      phase: 'Delivery',
      image: '/images/adaptive-training/AdaptiveTraining5.png',
    },
    {
      title: intl.formatMessage({id: 'home.adaptiveTraining.feature6Title'}),
      description: intl.formatMessage({
        id: 'home.adaptiveTraining.feature6Description',
      }),
      phase: 'Understanding',
      image: '/images/adaptive-training/AdaptiveTraining6.png',
    },
  ];

  // Training phases visualization
  const TrainingPhasesVisualization = () => (
    <div className="relative bg-gradient-to-br from-green-50 to-blue-50 rounded-3xl p-8">
      <h4 className="text-xl font-semibold text-center mb-8 text-gray-800">
        Training Phases Throughout Your Season
      </h4>

      <div className="flex justify-between items-center mb-6">
        {['Base', 'Peak', 'Test', 'Recovery', 'Maintenance'].map(
          (phase, index) => (
            <motion.div
              key={phase}
              className="text-center"
              // initial={{opacity: 0, y: 20}}
              // animate={isInView ? {opacity: 1, y: 0} : {}}
              // transition={{duration: 0.6, delay: 0.5 + index * 0.1}}
            >
              <motion.div
                className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-sm mb-2 ${
                  index === 0
                    ? 'bg-green-500'
                    : index === 1
                    ? 'bg-yellow-500'
                    : index === 2
                    ? 'bg-red-500'
                    : index === 3
                    ? 'bg-blue-500'
                    : 'bg-purple-500'
                }`}
                // initial={{scale: 0}}
                // animate={isInView ? {scale: 1} : {}}
                // transition={{duration: 0.5, delay: 0.8 + index * 0.1}}
              >
                {phase.slice(0, 1)}
              </motion.div>
              <div className="text-xs font-medium text-gray-700">{phase}</div>
            </motion.div>
          )
        )}
      </div>

      {/* Phase connection line */}
      <motion.div
        className="h-1 bg-gradient-to-r from-green-500 to-purple-500 rounded-full mb-6"
        // initial={{scaleX: 0}}
        // animate={isInView ? {scaleX: 1} : {}}
        // transition={{duration: 2, delay: 1.5}}
      />

      <p className="text-sm text-gray-600 text-center">
        Purposeful blocks structure your training for optimal improvement
      </p>
    </div>
  );

  // Weekly schedule visualization
  const WeeklyScheduleVisualization = () => (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <h4 className="text-lg font-semibold mb-4 text-gray-800">
        Your Week, Your Way
      </h4>

      <div className="grid grid-cols-7 gap-2 mb-4">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
          <motion.div
            key={index}
            className="text-center"
            // initial={{opacity: 0, scale: 0}}
            // animate={isInView ? {opacity: 1, scale: 1} : {}}
            // transition={{duration: 0.4, delay: 0.3 + index * 0.1}}
          >
            <div className="text-xs font-medium text-gray-600 mb-1">{day}</div>
            <motion.div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                index === 1 || index === 4
                  ? 'bg-red-100 text-red-600'
                  : index === 6
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-green-100 text-green-600'
              }`}
              whileHover={{scale: 1.1}}
            >
              {index === 1 || index === 4 ? 'W' : index === 6 ? 'L' : 'E'}
            </motion.div>
          </motion.div>
        ))}
      </div>

      <div className="flex justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-100 rounded-full"></div>
          <span className="text-gray-600">Easy</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-100 rounded-full"></div>
          <span className="text-gray-600">Workout</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-100 rounded-full"></div>
          <span className="text-gray-600">Long</span>
        </div>
      </div>
    </div>
  );

  return (
    <section
      ref={ref}
      className="py-20 bg-gradient-to-br from-gray-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-20 lg:mb-24"
          // initial={{opacity: 0, y: 50}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 0.8, ease: 'easeOut'}}
        >
          <motion.div
            className="text-center mb-6"
            // initial={{opacity: 0, y: 20}}
            // animate={isInView ? {opacity: 1, y: 0} : {}}
            // transition={{duration: 0.6, ease: 'easeOut'}}
          >
            <span className="inline-block bg-orange-500 text-white text-sm font-bold px-4 py-2 rounded-full uppercase tracking-wide">
              {intl.formatMessage({id: 'home.adaptiveTraining.badge'})}
            </span>
          </motion.div>
          <h2 className="text-4xl md:text-6xl font-light text-black mb-4">
            <strong className="font-bold">
              {intl.formatMessage({id: 'home.adaptiveTraining.title'})}
            </strong>
            .<br />
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-20">
            {intl.formatMessage({id: 'home.adaptiveTraining.subtitle'})}
          </p>

          {/* Lifestyle image placeholder */}
          <motion.div
            className="relative max-w-4xl mx-auto mb-8 bg-gradient-to-br from-gray-900 via-orange-900 rounded-3xl p-1"
            // initial={{opacity: 0, scale: 0.9}}
            // animate={isInView ? {opacity: 1, scale: 1} : {}}
            // transition={{duration: 1, delay: 0.3}}
          >
            <img
              src="/images/adaptive-training/AdaptiveTrainingFeature.png"
              alt="Runners training with adaptive guidance"
              className="w-full object-cover rounded-3xl"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-3xl" />
            <div className="absolute bottom-8 left-8 text-white">
              <p className="text-sm opacity-90">
                {intl.formatMessage({id: 'home.adaptiveTraining.description'})}
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Training visualizations */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{opacity: 0, x: -30}}
            animate={isInView ? {opacity: 1, x: 0} : {}}
            transition={{duration: 0.8, delay: 0.5}}
          >
            <TrainingPhasesVisualization />
          </motion.div>

          <motion.div
            initial={{opacity: 0, x: 30}}
            animate={isInView ? {opacity: 1, x: 0} : {}}
            transition={{duration: 0.8, delay: 0.7}}
          >
            <WeeklyScheduleVisualization />
          </motion.div>
        </div> */}

        {/* Feature Cards - Horizontal Scroll */}
        <div className="relative mb-16">
          <div
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
            style={{scrollSnapType: 'x mandatory'}}
          >
            {trainingFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className="flex-shrink-0 w-80 bg-white rounded-3xl overflow-hidden shadow-sm transition-all duration-300 hover:shadow-lg"
                style={{scrollSnapAlign: 'start'}}
                // initial={{opacity: 0, y: 30}}
                // animate={isInView ? {opacity: 1, y: 0} : {}}
                // transition={{duration: 0.6, delay: 0.4 + index * 0.1}}
              >
                {/* Image */}
                <div className="overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-gray-600 leading-relaxed text-base">
                    <span className="font-semibold pr-2">{feature.title}</span>
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Scroll indicator */}
          <div className="flex justify-center mt-6">
            <div className="flex gap-2">
              {trainingFeatures.map((_, index) => (
                <div key={index} className="w-2 h-2 bg-gray-300 rounded-full" />
              ))}
            </div>
          </div>
        </div>

        {/* Adjustment feature highlight */}
        {/* <motion.div
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 text-white text-center"
          initial={{opacity: 0, y: 30}}
          animate={isInView ? {opacity: 1, y: 0} : {}}
          transition={{duration: 0.8, delay: 1.5}}
        >
          <motion.h3
            className="text-3xl font-semibold mb-4"
            initial={{opacity: 0}}
            animate={isInView ? {opacity: 1} : {}}
            transition={{duration: 0.6, delay: 1.8}}
          >
            Adjust your training at any time
          </motion.h3>
          <motion.p
            className="text-xl opacity-90 max-w-3xl mx-auto mb-6"
            initial={{opacity: 0}}
            animate={isInView ? {opacity: 1} : {}}
            transition={{duration: 0.6, delay: 2.0}}
          >
            You can adjust on the fly without restarting. If you were too
            aggressive or conservative with your initial plan, you can adjust at
            any time.
          </motion.p>

          <motion.div
            className="flex justify-center"
            initial={{opacity: 0, scale: 0.8}}
            animate={isInView ? {opacity: 1, scale: 1} : {}}
            transition={{duration: 0.6, delay: 2.2}}
          >
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                  />
                </svg>
              </div>
              <div className="text-left">
                <div className="font-semibold">Real-time Adjustments</div>
                <div className="text-sm opacity-80">No restart required</div>
              </div>
            </div>
          </motion.div>
        </motion.div> */}
      </div>
    </section>
  );
};
